/**
 * Display names for document classes (production issue 15).
 *
 * The classifier's vocabulary is snake_case (`sow`, `change_order`, ...).
 * Screens used to title-case it, which gave "Sow" and showed "Unclassified"
 * for a document the classifier had simply not reached yet. One map, used
 * by every screen that shows a class: the document card, the library's
 * folders and groups, and the upload modal.
 */
export const DOCUMENT_CLASS_LABELS: Readonly<Record<string, string>> = {
  sow: 'SOW',
  change_order: 'Change order',
  master_agreement: 'Master agreement',
  order_form: 'Order form',
  status_report: 'Status report',
  test_report: 'Test report',
  test_exit_report: 'Test exit report',
  vendor_methodology: 'Vendor methodology',
  project_charter: 'Project charter',
  transcript: 'Transcript',
  interview_notes: 'Interview notes',
  // T-0234: plans and reports the health and go-live workers read.
  project_plan: 'Project plan',
  test_plan: 'Test plan',
  defect_report: 'Defect report',
  training_plan: 'Training plan',
  cutover_plan: 'Cutover plan',
  spend_report: 'Spend report',
  unknown: 'Unknown',
};

/** Shown while a document has no class yet. */
export const NOT_CLASSIFIED_LABEL = 'Not classified';

/**
 * The display name of a document class. `null`, empty and `unclassified`
 * mean the classifier has not run: "Not classified". A class the map does
 * not know is shown in sentence case ("Some new type") rather than hidden.
 */
export function documentClassLabel(type: string | null | undefined): string {
  const key = (type ?? '').trim().toLowerCase();
  if (key.length === 0 || key === 'unclassified') return NOT_CLASSIFIED_LABEL;
  const known = DOCUMENT_CLASS_LABELS[key];
  if (known) return known;
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
